import { useEffect, useRef, useState } from 'react';

// Only the "Tpx Rpx Bpx Lpx" form is used in this app.
function parseRootMargin(rootMargin) {
  const parts = String(rootMargin)
    .split(' ')
    .map((v) => parseFloat(v) || 0);
  const [top, right, bottom, left] = parts.length === 4 ? parts : [0, 0, 0, 0];
  return { top, right, bottom, left };
}

/**
 * Tracks whether the element currently intersects the given root, like
 * IntersectionObserver — but computed from getBoundingClientRect() on a
 * timer instead of the native API. IntersectionObserver doesn't reliably
 * re-fire for elements that only move via a CSS transform animation (no
 * real scroll/resize/mutation event) — engines vary in how eagerly they
 * re-check, which for a continuously-translating marquee like this one can
 * mean several elements appear to cross the threshold in the same batch
 * instead of one at a time. Polling rects directly is engine-independent.
 *
 * `threshold` is the visible ratio needed to *enter* view; `exitThreshold`
 * (default: the same) is the ratio it must drop to before it counts as out
 * again. Setting it lower gives hysteresis — e.g. `threshold: 0.6,
 * exitThreshold: 0` means "count as visible once 60% is showing, and keep
 * counting until it has left entirely".
 */
export function useInView({ root = null, rootMargin = '0px', threshold = 0, exitThreshold = threshold } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const rootEl = root && 'current' in root ? root.current : root;
    const margin = parseRootMargin(rootMargin);

    const check = () => {
      const elRect = el.getBoundingClientRect();
      const rootRect = rootEl
        ? rootEl.getBoundingClientRect()
        : { top: 0, left: 0, right: window.innerWidth, bottom: window.innerHeight };
      const bound = {
        top: rootRect.top - margin.top,
        bottom: rootRect.bottom + margin.bottom,
        left: rootRect.left - margin.left,
        right: rootRect.right + margin.right,
      };
      const overlapHeight = Math.max(0, Math.min(elRect.bottom, bound.bottom) - Math.max(elRect.top, bound.top));
      const overlapWidth = Math.max(0, Math.min(elRect.right, bound.right) - Math.max(elRect.left, bound.left));
      const overlapArea = overlapHeight * overlapWidth;
      const ratio = elRect.height > 0 && elRect.width > 0 ? overlapArea / (elRect.height * elRect.width) : 0;
      setInView((prev) => overlapArea > 0 && (prev ? ratio > exitThreshold : ratio >= threshold));
    };

    check();
    // 200ms is plenty of resolution for a slow (tens-of-seconds), continuous
    // marquee — no need for rAF-speed polling. Several of these run at once
    // (one per redacted card), so keeping this coarse matters for avoiding
    // main-thread contention with the decode animation's own DOM updates.
    const id = setInterval(check, 200);
    window.addEventListener('resize', check);
    return () => {
      clearInterval(id);
      window.removeEventListener('resize', check);
    };
  }, [root, rootMargin, threshold, exitThreshold]);

  return [ref, inView];
}

export default useInView;
